import StatusCode from 'status-code-enum';
import { useEffect } from 'react';

import { savedSearchApi } from '../../../../../api/savedSearches';

import { useUserContext } from '../../../../../contexts/UserContext';
import { useSavedSearchModalState } from '../../../../../contexts/SavedSearchModalContext';

import { Link } from '../../../../../components/Toolkit/Button/Link';

import { useOnUpdateOnly } from '../../../../../hooks/UseOnUpdateOnly';
import { useBrowserStorage } from '../../../../../hooks/UseBrowserStorage';

import { USER_MENU } from '../../../../../components/Layouts/components/UserMenu.typed';

import { fireToast } from '../../../../../helpers/Toasts';
import { formatAuthorizationHeader } from '../../../../../helpers/auth';
import { AUTHENTICATION_STATUSES } from '../../types';
import { isAxiosError } from 'axios';

enum NotificationFrequency {
  DAILY = 'DAILY',
  INSTANT = 'INSTANT',
  OFF = 'OFF',
}

type UseSavedSearchPanelArgs = { adId: number };

const useSavedSearchPanel = (args: UseSavedSearchPanelArgs) => {
  const { adId } = args;

  const {
    setIsSavedSearchModalOpen,
    setIsInstantAlertAvailable,
    isSearchSaved,
    setIsSearchSaved,
    setSelectedNotificationFrequency,
  } = useSavedSearchModalState();
  const { accessToken, status, handleLogin } = useUserContext();

  const authHeaders = formatAuthorizationHeader(accessToken);

  const savedSearchPageHref = `/dashboard/${USER_MENU.SEARCHES}`;

  const MODAL_SESSION_STORAGE_ITEM = 'ad-details-saved-search-modal';

  const {
    getSessionStorageItem,
    setSessionStorageItem,
    removeSessionStorageItem,
  } = useBrowserStorage();

  const SESSION_STORAGE_SIGNIN_ITEM_NAME = 'signin';

  const onOpen = async () => {
    if (status === AUTHENTICATION_STATUSES.AUTHENTICATED) {
      try {
        const {
          data: { instantAlert },
        } = await savedSearchApi.adDetailsPagePreCheck(adId, authHeaders);
        if (instantAlert) {
          setIsInstantAlertAvailable(true);
          setSelectedNotificationFrequency(NotificationFrequency.INSTANT);
        } else {
          setIsInstantAlertAvailable(false);
        }
        setIsSavedSearchModalOpen(true);
      } catch (error) {
        if (
          isAxiosError(error) &&
          error.response?.status === StatusCode.ClientErrorUnprocessableEntity
        ) {
          setIsSearchSaved(true);
          fireToast({
            type: 'ERROR',
            text: (
              <>
                You already have a Saved Search for these filters.&nbsp;
                <Link
                  ofType="SECONDARY"
                  inline
                  href={savedSearchPageHref}
                  underline
                >
                  View all Saved Searches
                </Link>
              </>
            ),
            closeOnClick: false,
          });
        } else
          fireToast({
            type: 'ERROR',
            text: 'Oops! Something went wrong, please try again later',
          });
      }
    } else if (status === AUTHENTICATION_STATUSES.UNAUTHENTICATED) {
      setSessionStorageItem(
        SESSION_STORAGE_SIGNIN_ITEM_NAME,
        MODAL_SESSION_STORAGE_ITEM,
      );
      handleLogin();
    }
  };

  useOnUpdateOnly(() => {
    if (status === AUTHENTICATION_STATUSES.UNAUTHENTICATED) {
      setIsSearchSaved(false);
    }
  }, [status]);

  useOnUpdateOnly(() => {
    setIsSearchSaved(false);
  }, [adId]);

  useEffect(() => {
    const isReturningUser =
      getSessionStorageItem(SESSION_STORAGE_SIGNIN_ITEM_NAME) ===
        MODAL_SESSION_STORAGE_ITEM &&
      status !== AUTHENTICATION_STATUSES.LOADING;

    if (isReturningUser) {
      if (status === AUTHENTICATION_STATUSES.AUTHENTICATED) {
        onOpen();
      }
      removeSessionStorageItem(SESSION_STORAGE_SIGNIN_ITEM_NAME);
    }
    // TODO: Clean up this effect's dependencies. We're disabling this lint error for now so we can
    // clean up the lint logs. Ideally we should rewrite this code to be less error prone and trust
    // the lint rule's judgement.
    // https://distilledsch.tpondemand.com/RestUI/Board.aspx#page=userstory/98606
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  return {
    isSearchSaved,
    onOpen,
  };
};

export { useSavedSearchPanel };
